// Sparse fractional ranking for the flagged-RFQ priority board.
//
// THE PROBLEM
// A manually ordered list needs a persisted position. The naive design is an
// integer `position` column, but reordering then rewrites every row after the
// insertion point: drag one item to the top of a 40-item board and you have
// issued 40 UPDATEs inside one request. That is slow, it is a write amplifier,
// and two concurrent reorders interleave into an order neither user asked for.
//
// THE APPROACH
// Ranks are sparse decimals seeded RANK_STEP apart (1000, 2000, 3000, …). To
// place an item between two neighbours you take the midpoint — ONE row changes,
// no matter how long the list is. Halving a gap forever eventually exhausts
// float precision, so `needsRebalance` reports when a gap has narrowed enough
// to warrant respacing the whole board (rare: from a 1000 gap it takes ~30
// consecutive drops into the same slot).
//
// Storage is `numeric(20,10)` — exact decimal, ~1e-10 resolution, so MIN_GAP of
// 1e-6 leaves four orders of magnitude of headroom. Deliberately NOT a float
// column: binary floats cannot represent decimal midpoints exactly, and an
// ordering column that rounds is an ordering column that eventually ties.
//
// PURE MODULE — no DB, no React. All of the board's real logic lives here so it
// is testable in the existing node harness, which is the whole reason this file
// exists separately from the component.

/** Gap between freshly seeded ranks. Large enough to absorb many midpoints. */
export const RANK_STEP = 1000;

/**
 * Narrowest gap tolerated before a rebalance is advised.
 *
 * numeric(20,10) resolves to 1e-10; stopping at 1e-6 keeps a wide margin and
 * means a rebalance is a routine housekeeping action rather than a rescue.
 */
export const MIN_GAP = 1e-6;

/** Rank of the very first item on an empty board. */
export const FIRST_RANK = RANK_STEP;

/**
 * The rank an item should take when dropped between `prev` and `next`.
 *
 * `null` means "no neighbour on that side" — i.e. the item is going to the very
 * top or the very bottom. Ranks ascend, so the smallest rank renders first.
 */
export function computeMidpointRank(prev: number | null, next: number | null): number {
  if (prev === null && next === null) return FIRST_RANK; // empty board
  if (prev === null) return next! - RANK_STEP;           // dropped at the top
  if (next === null) return prev + RANK_STEP;            // dropped at the bottom

  // Defensive: if the stored order is inconsistent (prev >= next), a midpoint
  // would silently land outside the intended slot. Step off `prev` instead so
  // the move still does something sane, and let needsRebalance flag the board.
  if (prev >= next) return prev + RANK_STEP;

  return (prev + next) / 2;
}

/**
 * True when the gap an item is being dropped into is too narrow to keep
 * subdividing. The caller should still apply the move, then rebalance.
 */
export function needsRebalance(prev: number | null, next: number | null): boolean {
  if (prev === null || next === null) return false; // unbounded end, always room
  return next - prev < MIN_GAP;
}

/** Evenly spaced ranks for `count` items — used to seed and to rebalance. */
export function seedRanks(count: number): number[] {
  return Array.from({ length: count }, (_, i) => (i + 1) * RANK_STEP);
}

/**
 * Respace an entire board back onto clean RANK_STEP boundaries, preserving the
 * current visual order. Returns one entry per id, in the order given.
 */
export function rebalanceRanks(orderedIds: string[]): Array<{ id: string; rank: number }> {
  return orderedIds.map((id, i) => ({ id, rank: (i + 1) * RANK_STEP }));
}

export interface MoveResult {
  /** The rank to persist for the moved item. */
  rank: number;
  /** Whether the board should be respaced after applying this move. */
  rebalance: boolean;
}

/**
 * Compute the new rank for an item dragged from `fromIndex` to `toIndex`.
 *
 * `currentRanks` is the board's ranks in DISPLAY ORDER, as it looked BEFORE the
 * drag. Indices are also pre-move — which is what dnd-kit's onDragEnd hands you.
 *
 * The neighbours are taken from the list with the moved item REMOVED, because
 * an item is never its own neighbour. Getting this wrong is the classic
 * off-by-one in drag ordering: comparing against the pre-removal array makes a
 * downward drag land one slot short.
 */
export function computeRankForMove(
  currentRanks: Array<number | null>,
  fromIndex: number,
  toIndex: number,
): MoveResult {
  const remaining = currentRanks.filter((_, i) => i !== fromIndex);

  // A null rank means "never ranked". Treat it as absent rather than coercing
  // to 0, which would otherwise drag an unranked row to the top of the board.
  const prev = toIndex - 1 >= 0 ? remaining[toIndex - 1] ?? null : null;
  const next = toIndex < remaining.length ? remaining[toIndex] ?? null : null;

  return {
    rank: computeMidpointRank(prev, next),
    rebalance: needsRebalance(prev, next),
  };
}

/**
 * Order a board client-side the way Postgres will: `board_rank ASC NULLS LAST`,
 * then newest-first among unranked rows so a freshly flagged RFQ that has not
 * been positioned yet still appears somewhere predictable.
 *
 * Exported so the optimistic UI update and the server agree on ordering without
 * a refetch — a mismatch here shows up as a row visibly jumping after a drag.
 */
export function sortByRank<T extends { boardRank: number | null; receivedAt: string }>(
  items: T[],
): T[] {
  return [...items].sort((a, b) => {
    if (a.boardRank !== null && b.boardRank !== null) return a.boardRank - b.boardRank;
    if (a.boardRank !== null) return -1;
    if (b.boardRank !== null) return 1;
    return b.receivedAt.localeCompare(a.receivedAt);
  });
}
